import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Scale } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Button } from "@/components/ui/button";
import { KeyFacts } from "@/components/country/KeyFacts";
import { BudgetTables } from "@/components/country/BudgetTables";
import { BudgetReview } from "@/components/country/BudgetReview";
import { ElectionCountdown } from "@/components/country/ElectionCountdown";
import { ElectionDateEditor } from "@/components/country/ElectionDateEditor";
import { ResultsBoard } from "@/components/country/ResultsBoard";
import { BudgetHistory } from "@/components/country/BudgetHistory";
import { BudgetShare } from "@/components/country/BudgetShare";
import { ResultsHistory } from "@/components/country/ResultsHistory";
import { PastBudgetEditor } from "@/components/country/PastBudgetEditor";

export const CountryBrief = ({ liveId }) => {
  const { me } = useMe();
  const admin = me?.role === "admin";
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/live/${liveId}/country`).then((r) => { setD(r.data); return r.data; }).catch(() => setD({}));
  useEffect(() => { load(); }, [liveId]); // eslint-disable-line react-hooks/exhaustive-deps
  const poll = async (key, before, ok) => {
    for (let i = 0; i < 24; i += 1) {
      await new Promise((res) => setTimeout(res, 8000));
      const n = await load();
      if (n?.[key] && n[key] !== before) { toast.success(ok); return; }
    }
    toast.message("Still working. Check back in a minute");
  };
  const refresh = async () => { setBusy(true); try { await api.post(`/admin/live/${liveId}/country/refresh`); toast.message("Refreshing key facts and topics (about a minute)"); await poll("topics_at", d.topics_at, "Key facts and topics updated"); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  if (!d?.country) return null;
  return (
    <section className="max-w-5xl space-y-4" data-testid="country-brief">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex-1 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--facts))]">{d.country.name} at a glance</p>
        {d.facts_at && <span className="w-full text-[11px] text-slate-500 sm:w-auto">Official statistics, updated daily · {fmtDate(d.facts_at)}</span>}
        {admin && <Button size="sm" variant="ghost" className="rounded-full" disabled={busy} onClick={refresh} data-testid="country-refresh-btn"><RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} />Refresh</Button>}
      </div>
      <ElectionCountdown election={d.election} country={d.country.name} action={admin && <ElectionDateEditor liveId={liveId} election={d.election} onSaved={load} />} />
      {d.results_board && <ResultsBoard b={d.results_board} liveId={liveId} edit={admin && { liveId, onSaved: (b) => setD({ ...d, results_board: b }) }} />}
      {d.results_board && admin && <ResultsHistory liveId={liveId} board={d.results_board} />}
      <Link to="?tab=perspective" className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--facts)/0.08)] px-3 py-1.5 text-sm font-medium text-[hsl(var(--facts))] hover:bg-[hsl(var(--facts)/0.14)]" data-testid="country-jump-perspective"><Scale size={14} />Middle's perspective on the topics that may decide the next election</Link>
      <KeyFacts facts={d.facts} official={d.official} />
      {d.official ? <BudgetTables o={d.official} approvedAt={d.approved_at} word={d.country.budget_word} population={d.facts?.population} /> : <p className="surface p-4 text-sm text-slate-500" data-testid="budget-pending">The {d.country.budget_word} budget table appears here once Middle's editors have checked it against the official budget papers.</p>}
      {d.official && <BudgetShare o={d.official} history={d.history} liveId={liveId} />}
      {admin && <BudgetReview liveId={liveId} d={d} onDone={load} poll={poll} />}
      {admin && d.official && <PastBudgetEditor liveId={liveId} d={d} onDone={load} poll={poll} />}
      <BudgetHistory history={d.history} word={d.country.budget_word} />
    </section>
  );
};
